import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { mdiClose } from '@mdi/js';
import Icon from '@mdi/react';
import { Modal, ModalBody, Table } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { fetchAllRevisions } from '../../../store/apis/travelRequestService';
import { Messages } from '../../../common/messages';

function ViewRevisionsModal({ isOpen, toggle, requestNo, revisionNo }) {
  const location = useLocation();
  // Add dependency tracking for parameters
  const isParamsReady = Boolean(requestNo && revisionNo && isOpen);
  const isHRPath = location.pathname.includes('/hr');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['revisionDetails', requestNo, revisionNo, isOpen],
    queryFn: () => fetchAllRevisions({ requestNo, revisionNo }),
    enabled: isParamsReady, // only fetch when modal is open
    onError: () => {
      toast.error('Failed to fetch revisions', {
        className: 'toast-error',
      });
    },
  });

  useEffect(() => {
    if (isParamsReady) {
      refetch();
    }
  }, [requestNo, revisionNo, isOpen, refetch, isParamsReady]);

  const renderTableBody = () => {
    const tableData = data;
    const linkPath = isHRPath
      ? '/hr/travel-request/edit' // currently in HR → link to Self-Service
      : '/travel-request/edit'; // currently in Self-Service → link to HR
    // Ensure tableData is not undefined or null
    if (!tableData || Object.keys(tableData).length === 0) {
      if (isLoading) {
        return (
          <tr>
            <td className="text-center" colSpan={4}>
              Loading...
            </td>
          </tr>
        );
      }
      return (
        <tr>
          <td className="text-center" colSpan={4}>
            {Messages.NO_REVISIONS}
          </td>
        </tr>
      );
    }

    return tableData?.map((item, index) => (
      <tr key={index}>
        <td className="py-2">{index + 1}.</td>
        <td className="py-2">
          <Link
            to={`${linkPath}/${requestNo}/${item?.RevisionNo ?? 0}?m=View`}
            className="text-primary"
          >
            {item?.RevisionNo ?? 'N/A'}
          </Link>
        </td>
        <td className="py-2">{item?.RevisionStatus ?? 'N/A'}</td>
        <td className="py-2">{item?.RevisionRemarks ?? 'N/A'}</td>
      </tr>
    ));
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl">
      <div className="modal-header p-0">
        <div className="pt-3 pb-2 px-3 d-flex flex-row align-items-center justify-content-between w-100 mb-2">
          <div className="fw-semibold">View Revisions</div>
          <button type="button" className="btn btn-link p-0" onClick={toggle}>
            <Icon path={mdiClose} size={0.8} />
          </button>
        </div>
      </div>
      <ModalBody className="p-3">
        <div
          className="table-responsive table-fix p-0"
          style={{
            border: '2px solid #D0D5DD',
            borderRadius: '8px',
          }}
        >
          <Table hover className="custom-table align-middle text-nowrap mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Revision Number</th>
                <th>Revision Status</th>
                <th>Revision Remarks</th>
              </tr>
            </thead>
            <tbody>{renderTableBody()}</tbody>
          </Table>
        </div>
      </ModalBody>
    </Modal>
  );
}

export default ViewRevisionsModal;
